/**
 * AccountCreateModal Component
 *
 * Modal for creating new trading accounts.
 * Collects required fields (name, broker, account type) and optional fields
 * (description, starting balance). Uses AccountsContext for mutation and
 * broker data, following BrokerModal's two-stage submit pattern.
 */

// 1. External dependencies
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

// 2. Workspace packages
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Input,
  NumberInput,
  Select,
  SelectItem,
  Alert,
} from '@enfomentis/ui';

// 3. Internal absolute imports
import { ACCOUNT_TYPE_OPTIONS } from '@/constants/accountType';
import type { AccountType } from '@/types/shared/AccountType';
import type { Account } from '@/types/schema/Account';

// 4. Relative imports
import { useAccounts } from '../context';

/**
 * Props for AccountCreateModal
 */
export interface AccountCreateModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Callback when modal closes */
  onClose: () => void;
  /** Callback after successful creation - receives new account ID */
  onSave: (newAccountId: string) => void | Promise<void>;
}

/**
 * Form data for account creation
 */
interface AccountCreateFormData {
  name: string;
  brokerId: string;
  accountType: AccountType;
  description: string;
  startingBalance: number | undefined;
}

/**
 * Initial empty form state
 */
const initialFormData: AccountCreateFormData = {
  name: '',
  brokerId: '',
  accountType: 'personal',
  description: '',
  startingBalance: undefined,
};

/**
 * AccountCreateModal Component
 */
export const AccountCreateModal: React.FC<AccountCreateModalProps> = ({
  isOpen,
  onClose,
  onSave,
}) => {
  // Form state
  const [formData, setFormData] = useState<AccountCreateFormData>(initialFormData);
  const [nameError, setNameError] = useState<string | null>(null);
  const [brokerError, setBrokerError] = useState<string | null>(null);
  const [balanceError, setBalanceError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Context & navigation
  const { createAccount, activeBrokers } = useAccounts();
  const hasBrokers = activeBrokers.length > 0;
  const navigate = useNavigate();

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setFormData(initialFormData);
      setNameError(null);
      setBrokerError(null);
      setBalanceError(null);
      setSubmitError(null);
    }
  }, [isOpen]);

  // Close handler
  const handleClose = useCallback((): void => {
    onClose();
  }, [onClose]);

  /**
   * Handle text/select field changes
   */
  const handleFieldChange = (field: keyof AccountCreateFormData, value: string): void => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (field === 'name' && nameError) {
      setNameError(null);
    }
    if (field === 'brokerId' && brokerError) {
      setBrokerError(null);
    }
  };

  /**
   * Handle starting balance changes from NumberInput
   */
  const handleBalanceChange = useCallback((value: number | undefined): void => {
    setFormData((prev) => ({ ...prev, startingBalance: value }));
    if (balanceError) {
      setBalanceError(null);
    }
  }, [balanceError]);

  /**
   * Validate form data
   */
  const validateForm = (): boolean => {
    let isValid = true;

    const trimmedName = formData.name.trim();
    if (!trimmedName) {
      setNameError('Name is required');
      isValid = false;
    } else {
      setNameError(null);
    }

    if (!formData.brokerId) {
      setBrokerError('Broker is required');
      isValid = false;
    } else {
      setBrokerError(null);
    }

    if (formData.startingBalance !== undefined && formData.startingBalance < 0) {
      setBalanceError('Starting balance must be 0 or greater');
      isValid = false;
    } else {
      setBalanceError(null);
    }

    return isValid;
  };

  /**
   * Handle form submission — two-stage pattern
   */
  const handleSubmit = async (): Promise<void> => {
    setSubmitError(null);

    if (!validateForm()) {
      return;
    }

    const trimmedName = formData.name.trim();
    let newAccount: Account | undefined;

    // Build Partial<Account> data with exactOptionalPropertyTypes compliance
    const data: Partial<Account> = {
      name: trimmedName,
      brokerId: formData.brokerId,
      accountType: formData.accountType,
      ...(formData.description.trim() ? { description: formData.description.trim() } : {}),
    };

    // Stage 1: Create account (hard failure — modal stays open)
    try {
      setIsSubmitting(true);
      newAccount = await createAccount(data, formData.startingBalance);
    } catch (error) {
      // Context already logs the error — just set inline alert
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to create account';
      setSubmitError(errorMessage);
      setIsSubmitting(false);
      return;
    }

    // Stage 2: Post-create callback (soft failure — close modal regardless)
    try {
      await onSave(newAccount.id);
    } catch (error) {
      console.error('Post-create callback failed:', error);
    } finally {
      setIsSubmitting(false);
      handleClose();
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      size="md"
      placement="top-center"
      isDismissable={!isSubmitting}
      hideCloseButton={isSubmitting}
    >
      <ModalContent>
        <ModalHeader>New Account</ModalHeader>

        <ModalBody className="gap-4">
          {/* Name */}
          <Input
            label="Name"
            placeholder="Enter account name"
            value={formData.name}
            onValueChange={(value) => handleFieldChange('name', value)}
            isRequired
            isInvalid={!!nameError}
            errorMessage={nameError}
            autoFocus
            className="[&_input]:text-base"
          />

          {/* Account Type */}
          <Select
            label="Account Type"
            placeholder="Select account type"
            selectedKeys={[formData.accountType]}
            onSelectionChange={(keys) => {
              if (keys === 'all') return;
              const selected = Array.from(keys)[0] as string | undefined;
              if (selected) {
                handleFieldChange('accountType', selected);
              }
            }}
            isRequired
          >
            {ACCOUNT_TYPE_OPTIONS.map((option) => (
              <SelectItem key={option.value} textValue={option.label}>
                {option.label}
              </SelectItem>
            ))}
          </Select>

          {/* Broker */}
          <Select
            label="Broker"
            placeholder="Select a broker"
            selectedKeys={formData.brokerId ? [formData.brokerId] : []}
            onSelectionChange={(keys) => {
              if (keys === 'all') return;
              const selected = Array.from(keys)[0] as string | undefined;
              if (selected) {
                handleFieldChange('brokerId', selected);
              }
            }}
            isRequired
            isInvalid={!!brokerError}
            errorMessage={brokerError}
            isDisabled={!hasBrokers}
            description={!hasBrokers ? 'No active brokers available' : undefined}
          >
            {activeBrokers.map((broker) => (
              <SelectItem key={broker.id}>{broker.name}</SelectItem>
            ))}
          </Select>
          <div className="flex justify-end -mt-2">
            <Button
              variant="light"
              size="sm"
              className="text-xs"
              onPress={() => {
                handleClose();
                navigate('/settings/portfolio/reference-data?tab=brokers');
              }}
              isDisabled={isSubmitting}
            >
              Manage Brokers
            </Button>
          </div>

          {/* Description */}
          <Input
            label="Description"
            placeholder="Optional notes about this account"
            value={formData.description}
            onValueChange={(value) => handleFieldChange('description', value)}
            className="[&_input]:text-base"
          />

          {/* Starting Balance */}
          <NumberInput
            label="Starting Balance"
            placeholder="0.00"
            value={formData.startingBalance}
            onValueChange={handleBalanceChange}
            minValue={0}
            isInvalid={!!balanceError}
            errorMessage={balanceError}
            formatOptions={{
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            }}
          />

          {/* Error Alert */}
          {submitError && (
            <Alert color="danger" variant="flat">
              {submitError}
            </Alert>
          )}
        </ModalBody>

        <ModalFooter>
          <Button variant="flat" onPress={handleClose} isDisabled={isSubmitting}>
            Cancel
          </Button>
          <Button color="primary" onPress={handleSubmit} isLoading={isSubmitting} isDisabled={!hasBrokers}>
            Create Account
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
